import React, { useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ExclamationCircleIcon, XIcon } from '@heroicons/react/outline';

/**
 * SendTriageLetterDialog
 * ----------------------
 * The admin team's "Send letter" confirmation on the Admin Handover screen
 * (TD-145, TDI-387). Confirming makes the letter visible in the service-user
 * portals and emails the recipients that it is ready.
 *
 * Recipients are FIXED: the outcome letter always goes to every service user
 * linked to the referral, the parent or carer and the SENCO (product
 * decision: the referrer is always sent the outcome letter, regardless of the
 * consent-to-share answer on the parent forms). The dialog therefore confirms
 * who will receive it, by name, rather than offering a choice.
 *
 * The confirmation reads per service (TDI-379 Build Spec, deliverable 3):
 * ADHD letters go with leaflets, so the dialog says "Send the triage outcome
 * letter and these leaflets to:" and lists them before the recipients; autism
 * signposting is written into the letter, so the autism dialog says "Send the
 * triage outcome letter to:" and lists the recipients alone. Which applies
 * is decided by the leaflets the caller passes (letterLeaflets.js reads the
 * pathway settings), never by this component. A leaflet with a file opens
 * from its title, so the admin can see what will go before confirming; one
 * without (not yet supplied) is named only.
 *
 * Only roles that actually have a linked participant on the referral are
 * listed, so the letter is never "sent" to someone who isn't there.
 */

/** Display labels for the service-user recipient roles. */
export const RECIPIENT_LABELS = {
  guardian: 'Parent or carer',
  school_user: 'SENCO',
};

/**
 * Participant statuses that mean the person is no longer on the referral: a
 * corrected address (superseded), a parent the SENCO removed, a disabled or
 * archived participant. The letter email skips them (TDI-684), so the dialog
 * does too.
 */
const GONE_STATUSES = ['superseded', 'removed', 'disabled', 'archived'];

const isCurrentParticipant = (p) =>
  !GONE_STATUSES.includes(String(p?.status || '').trim().toLowerCase());

/**
 * The recipients the letter will go to, derived from the referral's current
 * participants. Only roles with a current participant are returned, in a
 * stable order, each naming every current participant in that role (two
 * parents are both named) for confirmation.
 *
 * @param {Array<{role?: string, status?: string, firstName?: string, lastName?: string}>} participants
 * @returns {Array<{role: string, label: string, name: string}>}
 */
export function availableLetterRecipients(participants) {
  const list = (Array.isArray(participants) ? participants : []).filter(isCurrentParticipant);
  return ['guardian', 'school_user']
    .filter((role) => list.some((p) => p?.role === role))
    .map((role) => {
      const name = list
        .filter((x) => x?.role === role)
        .map((p) => `${p.firstName || ''} ${p.lastName || ''}`.trim())
        .filter(Boolean)
        .join(' and ');
      return { role, label: RECIPIENT_LABELS[role], name };
    });
}

export default function SendTriageLetterDialog({
  open,
  onOpenChange,
  recipients = [],
  alreadyShared = [],
  leaflets = [],
  sending = false,
  onConfirm,
}) {
  // Key the shared state on a stable string, not array identity — callers often
  // pass fresh array literals each render.
  const sharedKey = (alreadyShared || []).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sharedSet = useMemo(() => new Set(alreadyShared || []), [sharedKey]);

  const allRoles = recipients.map((r) => r.role);
  const canSend = allRoles.length > 0 && !sending;
  const leafletList = Array.isArray(leaflets) ? leaflets.filter((l) => l && l.title) : [];
  const withLeaflets = leafletList.length > 0;

  // The outcome letter must reach BOTH the parent/carer and the school
  // (ICB decision, RMS-195). When one of them has no linked participant yet,
  // sending is still possible but the gap is made unmissable.
  const missingRoles = Object.keys(RECIPIENT_LABELS).filter(
    (role) => !allRoles.includes(role),
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-white rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="flex items-start justify-between p-5 pb-3">
            <div className="text-left">
              <Dialog.Title className="text-base font-semibold text-gray-900">
                {withLeaflets
                  ? 'Send the triage outcome letter and these leaflets to:'
                  : 'Send the triage outcome letter to:'}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-gray-500 mt-0.5">
                They will be able to view it in their portal and will get an email letting them
                know it is ready.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="px-5 pb-2 space-y-2 text-left">
            {withLeaflets && (
              <ul className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 space-y-1" aria-label="Leaflets">
                {leafletList.map((l) => (
                  <li key={l.key || l.title} className="text-sm text-gray-900">
                    {l.url ? (
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm"
                      >
                        {l.title}
                      </a>
                    ) : (
                      l.title
                    )}
                  </li>
                ))}
              </ul>
            )}
            {recipients.length === 0 ? (
              <p className="text-sm text-gray-600">
                There is no parent or carer or SENCO linked to this referral yet, so the letter
                cannot be sent.
              </p>
            ) : (
              recipients.map((r) => (
                <div
                  key={r.role}
                  className="flex items-start gap-3 p-3 rounded-lg border border-gray-200"
                >
                  <span className="min-w-0 text-left">
                    <span className="block text-sm font-medium text-gray-900">
                      {r.label}
                      {sharedSet.has(r.role) && (
                        <span className="ml-2 text-xs font-normal text-green-700">
                          already sent
                        </span>
                      )}
                    </span>
                    {r.name && (
                      <span className="block text-xs text-gray-500">{r.name}</span>
                    )}
                  </span>
                </div>
              ))
            )}
            {recipients.length > 0 && missingRoles.length > 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
                <ExclamationCircleIcon
                  className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600"
                  aria-hidden="true"
                />
                <p className="text-xs text-amber-800 text-left">
                  Warning: the outcome letter must go to both the parent or carer and the
                  SENCO.{' '}
                  {missingRoles
                    .map((role) => `No ${RECIPIENT_LABELS[role].toLowerCase()} is linked to this referral yet`)
                    .join('. ')}
                  , so they will not receive it.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 p-5 pt-3">
            <Dialog.Close asChild>
              <button
                type="button"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              disabled={!canSend}
              onClick={() => onConfirm && onConfirm(allRoles)}
              className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Sending…' : 'Yes'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
